import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Users, Copy, Check, Share2, DollarSign, Trophy, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ReferralProgram = ({ userId, userName }) => {
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    pendingRewards: 0,
    earnedRewards: 0,
    referralList: []
  });
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchReferralData();
  }, [userId]);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('susu_token');
      const response = await fetch(`${API_URL}/api/referral/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setReferralCode(data.referral_code);
        setReferralStats({
          totalReferrals: data.total_referrals || 0,
          pendingRewards: data.pending_rewards || 0,
          earnedRewards: data.earned_rewards || 0,
          referralList: data.referral_list || []
        });
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = `${window.location.origin}?ref=${referralCode}`;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = referralCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success('Referral link copied!');
    } catch (err) {
      toast.info('Copy this link: ' + referralLink);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Susu Money!',
          text: `Join me on Susu Money and we both get $10! Use my code: ${referralCode}`,
          url: referralLink
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const handleWhatsAppShare = () => {
    const message = encodeURIComponent(
      `🎉 Join me on Susu Money - the community savings app!\n\n` +
      `Save together with friends and family. Use my referral code and we BOTH get $10!\n\n` +
      `My code: ${referralCode}\n\n` +
      `Sign up here: ${referralLink}`
    );
    const whatsappUrl = `https://wa.me/?text=${message}`;
    
    // Try to open WhatsApp
    const shareWindow = window.open(whatsappUrl, '_blank');
    
    if (!shareWindow || shareWindow.closed || typeof shareWindow.closed === 'undefined') {
      // Popup blocked - show fallback
      toast.info('Pop-up blocked! Tap to try again or copy the link', {
        action: {
          label: 'Open WhatsApp',
          onClick: () => window.location.href = whatsappUrl
        },
        duration: 5000
      });
      // Also copy the link
      handleCopyLink();
    } else {
      toast.success('Opening WhatsApp...');
    }
  };

  const handleWhatsAppDirect = (phoneNumber) => {
    // Send to specific contact
    const message = encodeURIComponent(
      `Hey! Join me on Susu Money and we BOTH get $10! 🎁\n\n` +
      `Use my code: ${referralCode}\n` +
      `${referralLink}`
    );
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const rewardTiers = [
    { referrals: 3, reward: '$10', icon: '🥉', name: 'Bronze' },
    { referrals: 10, reward: '$50', icon: '🥈', name: 'Silver' },
    { referrals: 25, reward: '$150', icon: '🥇', name: 'Gold' },
    { referrals: 50, reward: '$400', icon: '💎', name: 'Diamond' }
  ];

  const currentTier = rewardTiers.reduce((acc, tier) => {
    if (referralStats.totalReferrals >= tier.referrals) return tier;
    return acc;
  }, null);

  const nextTier = rewardTiers.find(tier => referralStats.totalReferrals < tier.referrals);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-stone-200 animate-pulse">
        <div className="h-8 bg-stone-200 rounded w-1/3 mb-4"></div>
        <div className="h-24 bg-stone-100 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Gift className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Referral Program</h2>
            <p className="text-indigo-200 text-sm">Invite friends, earn rewards!</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-indigo-50 rounded-xl p-4 text-center">
            <Users className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-indigo-700">{referralStats.totalReferrals}</div>
            <div className="text-xs text-indigo-600">Friends Joined</div>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 text-center">
            <DollarSign className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-amber-700">${referralStats.pendingRewards}</div>
            <div className="text-xs text-amber-600">Pending</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <Trophy className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-green-700">${referralStats.earnedRewards}</div>
            <div className="text-xs text-green-600">Total Earned</div>
          </div>
        </div>

        {/* Referral Code Box */}
        <div className="bg-gradient-to-r from-stone-50 to-stone-100 rounded-xl p-4 mb-6">
          <div className="text-sm text-stone-600 mb-2">Your Referral Code</div>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-white rounded-lg px-4 py-3 font-mono text-xl font-bold text-indigo-600 border-2 border-dashed border-indigo-200">
              {referralCode}
            </div>
            <Button
              onClick={handleCopyCode}
              variant="outline"
              className="px-4"
            >
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Button
            onClick={handleWhatsAppShare}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Share on WhatsApp
          </Button>
          <Button
            onClick={handleShare}
            variant="outline"
          >
            <Share2 className="w-5 h-5 mr-2" />
            Share Link
          </Button>
        </div>

        {/* How It Works */}
        <div className="bg-amber-50 rounded-xl p-4 mb-6">
          <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            How It Works
          </h3>
          <div className="space-y-2 text-sm text-amber-900">
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 bg-amber-200 rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <span>Share your code with friends & family</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 bg-amber-200 rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <span>They sign up and join their first club</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 bg-amber-200 rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <span>You BOTH get $10 bonus! 🎉</span>
            </div>
          </div>
        </div>

        {/* Reward Tiers */}
        <div className="border-t border-stone-200 pt-6">
          <h3 className="font-bold text-stone-900 mb-4">Reward Tiers</h3>
          <div className="grid grid-cols-4 gap-2">
            {rewardTiers.map((tier, index) => (
              <div 
                key={index}
                className={`rounded-xl p-3 text-center transition-all ${
                  referralStats.totalReferrals >= tier.referrals 
                    ? 'bg-indigo-100 border-2 border-indigo-400' 
                    : 'bg-stone-50 border border-stone-200'
                }`}
              >
                <div className="text-2xl mb-1">{tier.icon}</div>
                <div className="text-xs font-bold text-stone-700">{tier.name}</div>
                <div className="text-sm font-bold text-indigo-600">{tier.reward}</div>
                <div className="text-[10px] text-stone-500">{tier.referrals} referrals</div>
              </div>
            ))}
          </div>
          
          {nextTier && (
            <div className="mt-4 bg-indigo-50 rounded-lg p-3 text-center">
              <p className="text-sm text-indigo-700">
                <span className="font-bold">{nextTier.referrals - referralStats.totalReferrals}</span> more referrals to unlock{' '}
                <span className="font-bold">{nextTier.icon} {nextTier.name}</span> tier!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralProgram;
